import React, { useEffect, useState } from 'react';
import { TextField, CircularProgress, Box, Autocomplete } from '@mui/material';
import { useJsApiLoader } from '@react-google-maps/api';

// Get API key from environment
const GOOGLE_MAPS_API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || '';
const libraries: ("places" | "drawing" | "geometry" | "localContext" | "visualization")[] = ['places'];

interface PlaceResult {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface AddressComponents {
  streetNumber?: string;
  route?: string;
  locality?: string;
  administrativeAreaLevel1?: string;
  postalCode?: string;
  country?: string;
}

interface ParsedAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  latitude?: number;
  longitude?: number;
}

interface GooglePlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (address: ParsedAddress) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

export const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  value,
  onChange,
  onAddressSelect,
  label = 'Street Address',
  placeholder = 'Start typing an address...',
  disabled = false,
  required = false,
}) => {
  const [predictions, setPredictions] = useState<PlaceResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: 'google-maps-script-places',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries,
  });

  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null);

  // Initialize services when Google Maps is loaded
  useEffect(() => {
    if (isLoaded && window.google) {
      setAutocompleteService(new window.google.maps.places.AutocompleteService());
      // Create a dummy element for PlacesService (required by Google Maps API)
      const dummyElement = document.createElement('div');
      setPlacesService(new window.google.maps.places.PlacesService(dummyElement));
    }
  }, [isLoaded]);

  // Handle input change and trigger predictions
  const handleInputChange = (event: React.SyntheticEvent, newValue: string) => {
    onChange(newValue);

    if (newValue.length > 2 && autocompleteService) {
      setIsLoading(true);

      const request = {
        input: newValue,
        types: ['address'],
        componentRestrictions: { country: ['ca', 'us'] }, // Restrict to Canada and US
      };

      autocompleteService.getPlacePredictions(request, (predictions, status) => {
        setIsLoading(false);
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setPredictions(
            predictions.map(prediction => ({
              place_id: prediction.place_id,
              description: prediction.description,
              structured_formatting: prediction.structured_formatting,
            }))
          );
        } else {
          setPredictions([]);
        }
      });
    } else {
      setPredictions([]);
    }
  };

  // Parse Google Places address components
  const parseAddressComponents = (
    components: google.maps.GeocoderAddressComponent[]
  ): AddressComponents => {
    const parsed: AddressComponents = {};

    components.forEach(component => {
      const types = component.types;

      if (types.includes('street_number')) {
        parsed.streetNumber = component.long_name;
      } else if (types.includes('route')) {
        parsed.route = component.long_name;
      } else if (types.includes('locality')) {
        parsed.locality = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        parsed.administrativeAreaLevel1 = component.short_name;
      } else if (types.includes('postal_code')) {
        parsed.postalCode = component.long_name;
      } else if (types.includes('country')) {
        parsed.country = component.long_name;
      }
    });

    return parsed;
  };

  // Handle place selection
  const handlePlaceSelect = async (
    event: React.SyntheticEvent,
    selectedPrediction: PlaceResult | null
  ) => {
    if (!selectedPrediction || !placesService) {
      return;
    }

    setIsLoading(true);

    try {
      const request = {
        placeId: selectedPrediction.place_id,
        fields: ['address_components', 'geometry', 'formatted_address'],
      };

      placesService.getDetails(request, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          const components = parseAddressComponents(place.address_components || []);

          // Build street address
          const street =
            [components.streetNumber, components.route].filter(Boolean).join(' ') ||
            selectedPrediction.description;

          const parsedAddress: ParsedAddress = {
            street: street,
            city: components.locality || '',
            state: components.administrativeAreaLevel1 || '',
            zipCode: components.postalCode || '',
            country: components.country || 'Canada',
            latitude: place.geometry?.location?.lat(),
            longitude: place.geometry?.location?.lng(),
          };

          onChange(street);
          onAddressSelect(parsedAddress);
        }
        setIsLoading(false);
      });
    } catch (error) {
      console.error('Error getting place details:', error);
      setIsLoading(false);
    }
  };

  if (!isLoaded) {
    return (
      <TextField
        fullWidth
        label={label}
        value=""
        disabled={true}
        placeholder="Loading Google Maps..."
        InputProps={{
          endAdornment: <CircularProgress size={20} />,
        }}
      />
    );
  }

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={predictions}
      getOptionLabel={option => (typeof option === 'string' ? option : option.description)}
      value={null} // Always null to allow free text input
      inputValue={value}
      onInputChange={handleInputChange}
      onChange={handlePlaceSelect}
      loading={isLoading}
      disabled={disabled}
      freeSolo
      filterOptions={x => x} // Don't filter, use Google's results
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          label={label}
          placeholder={placeholder}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isLoading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </Box>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.place_id}>
          <Box>
            <div style={{ fontWeight: 'bold' }}>{option.structured_formatting.main_text}</div>
            <div style={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              {option.structured_formatting.secondary_text}
            </div>
          </Box>
        </li>
      )}
    />
  );
};
